import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Quote as QuoteIcon, Plus, Trash2, Search, ExternalLink, Edit2, X, Save, Settings2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { WsPageTitle } from "./WorkspaceLayout";
import CategoryManager, { CategoryBadge, CategorySelect, CategorySidebar, useCategories, matchCategoryFilter } from "../../components/workspace/CategoryManager";

const empty = () => ({ id: null, text: "", author: "", source_url: "", category: "" });

export default function Quotes() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const { categories } = useCategories("quotes");

  const { data = [] } = useQuery({
    queryKey: ["workspace", "quotes", q],
    queryFn: () => api.get("/admin/workspace/quotes", { params: { q } }).then((r) => r.data),
  });

  // `cat` holds a category ID selected from the sidebar; the matcher handles sub-trees.
  const filtered = data.filter((qq) => matchCategoryFilter(qq.category, cat, categories));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["workspace", "quotes"] });
    qc.invalidateQueries({ queryKey: ["workspace", "overview"] });
  };

  const save = async () => {
    const errs = {};
    if (!editing.text || !editing.text.trim()) errs.text = "Alıntı metni gerekli";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error(`Eksik alan: ${Object.values(errs).join(", ")}`);
      return;
    }
    try {
      const payload = { text: editing.text, author: editing.author, source_url: editing.source_url, category: editing.category };
      if (editing.id) {
        await api.put(`/admin/workspace/quotes/${editing.id}`, payload);
        toast.success("Alıntı güncellendi");
      } else {
        await api.post("/admin/workspace/quotes", payload);
        toast.success("Alıntı eklendi");
      }
      setEditing(null);
      setErrors({});
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Alıntı silinsin mi?")) return;
    try { await api.delete(`/admin/workspace/quotes/${id}`); invalidate(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="ws-quotes">
      <WsPageTitle
        icon={QuoteIcon}
        title="Alıntılarım"
        sub="Beğendiğin cümleler, kaynaklar ve referanslar."
        action={
          <>
            <Button variant="outline" size="sm" onClick={() => setCatMgrOpen(true)} data-testid="ws-quote-cat-mgr">
              <Settings2 size={13} className="mr-1" /> Kategoriler
            </Button>
            <Button onClick={() => { setEditing(empty()); setErrors({}); }} data-testid="ws-quote-new">
              <Plus size={14} className="mr-1" />Yeni alıntı
            </Button>
          </>
        }
      />

      {/* Sidebar + content */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4" data-testid="ws-quotes-layout">
        <CategorySidebar
          kind="quotes"
          items={data}
          value={cat}
          onChange={setCat}
          onManage={() => setCatMgrOpen(true)}
          accent="sky"
          allLabel="Tüm Alıntılar"
        />

        <div className="min-w-0 space-y-4">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-3 text-stone-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Alıntı veya yazar..." className="pl-9" data-testid="ws-quote-search" />
            </div>
          </div>

          {/* Editor */}
          {editing && (
            <div className="bg-white rounded-2xl border border-sky-200 p-4 shadow-sm" data-testid="ws-quote-editor">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-slate-900">{editing.id ? "Alıntıyı düzenle" : "Yeni alıntı"}</p>
                <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-800"><X size={16} /></button>
              </div>
              <textarea
                value={editing.text}
                onChange={(e) => { setEditing({ ...editing, text: e.target.value }); if (errors.text) setErrors((er) => ({ ...er, text: null })); }}
                placeholder="Alıntı metni..."
                className={`w-full border rounded-lg p-3 text-sm font-serif leading-relaxed min-h-[120px] mb-1 outline-none focus:ring-1 ${errors.text ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "focus:border-sky-400 focus:ring-sky-200"}`}
                data-testid="ws-quote-text"
              />
              {errors.text && (
                <p className="text-xs text-red-600 mb-2" data-testid="ws-quote-text-error">{errors.text}</p>
              )}
              <div className="grid sm:grid-cols-3 gap-2 mt-2">
                <Input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} placeholder="Yazar / konuşmacı" data-testid="ws-quote-author" />
                <CategorySelect
                  value={editing.category}
                  onChange={(v) => setEditing({ ...editing, category: v })}
                  kind="quotes"
                  testid="ws-quote-category"
                  onManage={() => setCatMgrOpen(true)}
                  placeholder="Kategori"
                />
                <Input value={editing.source_url} onChange={(e) => setEditing({ ...editing, source_url: e.target.value })} placeholder="Kaynak URL (ops.)" data-testid="ws-quote-url" />
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button onClick={save} data-testid="ws-quote-save"><Save size={13} className="mr-1" />Kaydet</Button>
                <Button variant="outline" onClick={() => setEditing(null)}>İptal</Button>
              </div>
            </div>
          )}

          <ul className="grid md:grid-cols-2 gap-3">
            {filtered.map((qq) => (
              <li key={qq.id} className="bg-white rounded-xl border border-sky-100 p-4 shadow-sm relative group" data-testid={`ws-quote-item-${qq.id}`}>
                <QuoteIcon size={20} className="text-sky-300 absolute top-3 right-3" />
                <p className="font-serif text-slate-800 leading-relaxed italic text-[15px]">"{qq.text}"</p>
                <div className="flex items-center justify-between gap-2 mt-3 text-xs text-stone-600">
                  <div className="flex items-center gap-2 flex-wrap">
                    {qq.author && <span className="font-medium">— {qq.author}</span>}
                    {qq.category && (
                      <CategoryBadge
                        name={qq.category}
                        color={categories.find((c) => c.name === qq.category)?.color || "sky"}
                      />
                    )}
                    {qq.source_url && <a href={qq.source_url} target="_blank" rel="noopener noreferrer" className="text-sky-700 hover:underline inline-flex items-center gap-1"><ExternalLink size={10} />kaynak</a>}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition">
                    <button onClick={() => { setEditing(qq); setErrors({}); }} className="p-1 text-stone-400 hover:text-sky-700" title="Düzenle"><Edit2 size={13} /></button>
                    <button onClick={() => remove(qq.id)} className="p-1 text-stone-400 hover:text-red-600" title="Sil" data-testid={`ws-quote-del-${qq.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              </li>
            ))}
            {filtered.length === 0 && <li className="text-sm text-stone-400 italic p-4 bg-white rounded-xl border col-span-full">Alıntı yok.</li>}
          </ul>
        </div>
      </div>

      <CategoryManager
        open={catMgrOpen}
        onClose={() => setCatMgrOpen(false)}
        kind="quotes"
        title="Alıntı kategorilerini yönet"
      />
    </div>
  );
}
